import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Pencil, CheckCircle2, Loader2, Gift, Rocket, TrendingUp, Crown,
  Infinity as InfinityIcon, Info, WifiOff, Sparkles, AlertTriangle,
  Plus, Trash2, Power, PowerOff,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import LoadError from "@/components/dashboard/LoadError";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { getPlans, updatePlans, createPlan, deletePlan } from "@/api/admin";

// Ordering and delete-eligibility now come from the plan record itself
// (`sort_order`, `is_system`) rather than from a hardcoded list of the four ids
// that used to be defined in Python. A plan an administrator creates can be
// placed anywhere in the catalog, and a future system plan needs no edit here.
const SYSTEM_DELETE_HINT =
  "Plans that ship with the product can't be deleted. Deactivate it instead to stop offering it.";
const UNLIMITED = 999999;
// The backend accepts up to 100 chars (its own hard data-integrity bound),
// but the card this name renders on was never designed for anything close to
// that — a name much shorter than the backend's own limit already overlaps
// the card's badges (#82/#83). This is the actual UI-driven limit; the
// backend's 100 stays the outer safety net if this check is ever bypassed.
const PLAN_NAME_MAX_LEN = 40;

// A freshly opened "Add new plan" form (#81) — unlike editing, every field
// starts at a real, concrete value rather than "blank means leave alone",
// since there is nothing yet to leave alone.
const BLANK_PLAN_FORM = {
  name: "", label: "", price_inr: "", price_inr_yearly: "", active: true,
  max_runs_per_month: 0, max_projects: 0, max_testers: 0, trial_days: 0,
  ai_model_tier: "basic", reports: "", api_keys: 0,
  blurb: "", cta: "Subscribe", featured: false, perks: "", sort_order: 100,
  is_trial: false, is_signup_pack: false, is_default_signup_plan: false,
};

// `perks` is a list on the wire and one-per-line in the textarea — the only
// shape a plain <textarea> can offer, and the same convention the rest of the
// admin surfaces use for short lists.
const perksToText = (v) => (Array.isArray(v) ? v.join("\n") : String(v || ""));
const textToPerks = (v) => String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);

// Shared by the Edit and Add-new forms — same fields, same bounds. `blankOk`
// (edit only) lets an untouched field mean "leave this alone" rather than
// "reject it"; a new plan has no existing value to leave alone, so every
// field is checked as entered.
function planFormErrors(values, { blankOk = false } = {}) {
  const errors = {};
  const nameTrimmed = String(values.name || "").trim();
  if (!nameTrimmed) errors.name = "Give the plan a name.";
  else if (nameTrimmed.length > PLAN_NAME_MAX_LEN) errors.name = `Plan name can't be longer than ${PLAN_NAME_MAX_LEN} characters.`;
  const numeric = [
    ["price_inr", "Price", { max: 10000000 }],
    ["price_inr_yearly", "Yearly price", { max: 10000000 }],
    ["max_runs_per_month", "Max runs/mo", { integer: true }],
    ["max_projects", "Projects", { integer: true }],
    ["max_testers", "Testers", { integer: true }],
    ["trial_days", "Trial days", { integer: true, max: 365 }],
    ["api_keys", "API keys", { integer: true }],
  ];
  for (const [key, label, rule] of numeric) {
    const raw = values[key];
    if (isBlank(raw)) {
      if (blankOk) continue;                // edit: blank = leave unchanged
      if (key === "price_inr" || key === "price_inr_yearly") continue; // blank = "Custom"
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) { errors[key] = `${label} must be a number of 0 or more.`; continue; }
    if (rule.integer && !Number.isInteger(n)) { errors[key] = `${label} must be a whole number.`; continue; }
    if (rule.max && n > rule.max) { errors[key] = `${label} can't be more than ${rule.max.toLocaleString("en-IN")}.`; continue; }
  }
  return errors;
}

const DEMO_CATALOG = {
  free_trial: { name: "Free Trial", label: "FREE TRIAL", price_inr: 0, price_inr_yearly: 0, active: true, max_runs_per_month: 50, max_projects: 1, max_testers: 2, trial_days: 14, ai_model_tier: "basic", reports: "basic", api_keys: 0 },
  starter: { name: "Starter Plan", label: "STARTER", price_inr: 3999, price_inr_yearly: 38390, active: true, max_runs_per_month: 500, max_projects: 5, max_testers: 5, trial_days: 0, ai_model_tier: "basic", reports: "XLSX + PDF", api_keys: 1 },
  growth: { name: "Growth Plan", label: "GROWTH", price_inr: 9999, price_inr_yearly: 95990, active: true, max_runs_per_month: 5000, max_projects: 25, max_testers: 20, trial_days: 0, ai_model_tier: "advanced", reports: "XLSX + PDF", api_keys: 10 },
  custom: { name: "Custom Plan", label: "CUSTOM", price_inr: null, price_inr_yearly: null, active: true, max_runs_per_month: UNLIMITED, max_projects: UNLIMITED, max_testers: UNLIMITED, trial_days: 0, ai_model_tier: "advanced", reports: "XLSX + PDF", api_keys: UNLIMITED },
};

// Flat 20% off monthly×12 (#89/#90) — the same fixed policy the backend's
// DEFAULT_CATALOG figures were computed from. Only used for the "Auto"
// button below; the actual price an admin saves is whatever they end up with.
const yearlyAuto = (monthly) => Math.round(Number(monthly || 0) * 12 * 0.8);

const PLAN_STYLE = {
  free_trial: { icon: Gift, accent: "from-slate-400 to-slate-500", chip: "bg-slate-500/10 text-slate-600 dark:text-slate-300" },
  starter: { icon: Rocket, accent: "from-brand-500 to-indigo-500", chip: "bg-brand-500/10 text-brand-600 dark:text-brand-300" },
  growth: { icon: TrendingUp, accent: "from-fuchsia-500 to-violet-500", chip: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300" },
  custom: { icon: Crown, accent: "from-amber-500 to-orange-500", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-300" },
};

const fmtLimit = (n) => (Number(n) >= UNLIMITED ? "Unlimited" : Number(n || 0).toLocaleString("en-IN"));

// Blank is a distinct intent from 0 everywhere on this page: blank leaves the
// stored value alone, 0 is a real limit that blocks every company on the plan.
const isBlank = (v) => v === "" || v === null || v === undefined;

// The three quotas the backend actually enforces, used for the zero-limit guard
// and the Unlimited shortcuts.
const LIMIT_FIELDS = [
  { key: "max_runs_per_month", label: "Max runs / month" },
  { key: "max_projects", label: "Projects" },
  { key: "max_testers", label: "Tester seats" },
];
const priceDisplay = (v) => (v === null || v === "" || v === undefined ? "Custom" : Number(v) === 0 ? "Free" : `₹${Number(v).toLocaleString("en-IN")}.00`);

function cardFeatures(p) {
  // Driven by the plan's own is_trial flag, not by its id — an administrator can
  // now create additional trial plans, and they show trial days like this one.
  if (p.is_trial || p.id === "free_trial") return [
    { k: "Trial days", v: String(p.trial_days ?? 0) },
    { k: "Max runs / mo", v: fmtLimit(p.max_runs_per_month) },
    { k: "Projects", v: fmtLimit(p.max_projects) },
    { k: "AI model tier", v: p.ai_model_tier, cap: true },
    { k: "Reports", v: p.reports },
  ];
  return [
    { k: "Max runs / mo", v: fmtLimit(p.max_runs_per_month) },
    { k: "Projects", v: fmtLimit(p.max_projects) },
    { k: "AI model tier", v: p.ai_model_tier, cap: true },
    { k: "Reports", v: p.reports },
    { k: "API keys", v: fmtLimit(p.api_keys) },
  ];
}

export default function Plans() {
  const { data, loading, demo, error, setData } = useApiData(getPlans, DEMO_CATALOG);
  // Only the unreachable-backend path may show the fixture. On a 403/500 `data`
  // is null with `error` set, and falling back here presented invented prices
  // and limits as the live catalog an admin could then edit.
  // Memoised: the `{}` branch is a fresh object every render, which made the
  // useMemo below recompute (and its result change identity) on every render.
  const catalog = useMemo(
    () => (data && typeof data === "object" ? data : demo ? DEMO_CATALOG : {}),
    [data, demo],
  );
  // Same key the API sorts by (billing.list_plans), so the admin grid and the
  // pricing page can never disagree about order. Ties break on name then id so
  // the sequence is stable rather than dependent on object insertion order.
  const plans = useMemo(
    () => Object.entries(catalog)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100)
        || String(a.name || "").localeCompare(String(b.name || ""))
        || String(a.id).localeCompare(String(b.id))),
    [catalog]);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  // Per-field, keyed by form key — {name: "message", max_runs_per_month: "message", ...}.
  // Kept separate from saveError (the SERVER's response), which is always a
  // single event and has nothing to attach to a specific input.
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ---- Add new plan (#81) ---------------------------------------------------
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_PLAN_FORM);
  const [createBusy, setCreateBusy] = useState(false);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [createSaveError, setCreateSaveError] = useState("");

  // ---- Delete plan (#85) -----------------------------------------------------
  const [deleteTarget, setDeleteTarget] = useState(null);   // the plan object, or null
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // ---- Activate / Deactivate --------------------------------------------------
  // Its own confirmation target, kept apart from `editing` so toggling a plan's
  // availability never opens (or half-fills) the edit form.
  const [statusTarget, setStatusTarget] = useState(null);   // the plan object, or null
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState("");
  const savedTimer = useRef(null);
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  function openEdit(p) {
    setEditing(p.id);
    setSaveError("");
    setFieldErrors({});
    setForm({
      name: p.name || "",
      label: p.label || "",
      blurb: p.blurb || "",
      cta: p.cta || "Subscribe",
      featured: !!p.featured,
      perks: perksToText(p.perks),
      sort_order: p.sort_order ?? 100,
      is_trial: !!p.is_trial,
      is_signup_pack: !!p.is_signup_pack,
      is_default_signup_plan: !!p.is_default_signup_plan,
      price_inr: p.price_inr === null || p.price_inr === undefined ? "" : p.price_inr,
      price_inr_yearly: p.price_inr_yearly === null || p.price_inr_yearly === undefined ? "" : p.price_inr_yearly,
      active: p.active ?? true,
      max_runs_per_month: p.max_runs_per_month ?? 0,
      max_projects: p.max_projects ?? 0,
      max_testers: p.max_testers ?? 0,
      trial_days: p.trial_days ?? 0,
      ai_model_tier: p.ai_model_tier || "basic",
      reports: p.reports || "",
      api_keys: p.api_keys ?? 0,
    });
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Every field here is a bare number input with no bounds, so a typo or a
  // negative used to be persisted straight into the live catalog that all
  // enforcement resolves against.
  //
  // BLANK MEANS "LEAVE THIS FIELD ALONE". It used to be documented as "unlimited
  // / default" here and then coerced to 0 in the payload (`Number("") || 0`), so
  // clearing the runs box saved a limit of ZERO and every company on the plan was
  // instantly blocked from running a single test. Blank is now omitted from the
  // request entirely — the endpoint merges what it is sent (exclude_unset), so an
  // absent key genuinely means "unchanged". Unlimited is the explicit sentinel.
  // Checks EVERY field and returns all of them, not just the first — a form
  // that stops at the first bad field silently hides every other one, so
  // fixing "Max runs" and resubmitting just surfaced "Testers" as a brand
  // new surprise instead of having said so the first time (#79/#80). Shared
  // with the Add-new form via planFormErrors() above; editing always passes
  // blankOk since an untouched field here means "leave it alone".

  // Limits that would be saved as 0 — every company on the plan hits the cap
  // immediately. 0 is a legal value someone may genuinely want, so this warns
  // and asks rather than refusing; what it must never be is a silent accident.
  const zeroLimits = LIMIT_FIELDS
    .filter(({ key }) => !isBlank(form[key]) && Number(form[key]) === 0)
    .map(({ label }) => label);

  // What this save would actually change, old -> new, for the confirmation. Built
  // against the LOADED catalog, so untouched fields never appear.
  const pendingChanges = useMemo(() => {
    if (!editing) return [];
    const before = catalog[editing] || {};
    const rows = [];
    const label = {
      // No `active` row: this form no longer submits it (see saveEdit), so
      // listing it here could only ever show a change that isn't being made.
      name: "Name", price_inr: "Monthly price", price_inr_yearly: "Yearly price",
      max_runs_per_month: "Max runs / month", max_projects: "Projects",
      max_testers: "Tester seats", trial_days: "Trial days",
      ai_model_tier: "AI model tier", reports: "Reports", api_keys: "API keys",
    };
    Object.entries(label).forEach(([key, text]) => {
      const raw = form[key];
      if (isBlank(raw) && typeof raw !== "boolean") return;   // untouched
      const now = typeof raw === "boolean" ? raw : (Number.isNaN(Number(raw)) ? raw : Number(raw));
      const was = before[key];
      if (String(was ?? "") === String(now ?? "")) return;
      rows.push({ key, label: text, from: was, to: now });
    });
    return rows;
  }, [editing, form, catalog]);

  function requestSave() {
    const errors = planFormErrors(form, { blankOk: true });
    setFieldErrors(errors);
    if (Object.keys(errors).length) { setSaveError(""); return; }
    setSaveError("");
    setConfirmOpen(true);
  }

  async function saveEdit() {
    if (busy) return;
    const errors = planFormErrors(form, { blankOk: true });
    setFieldErrors(errors);
    if (Object.keys(errors).length) { setConfirmOpen(false); return; }
    setBusy(true);
    // Blank fields are OMITTED, never coerced. `Number("") || 0` is what turned
    // "I didn't touch this" into "set this to zero".
    const num = (v) => (isBlank(v) ? undefined : Number(v));
    const fields = {
      name: form.name.trim(),
      label: (form.label || "").trim() || undefined,
      blurb: form.blurb ?? undefined,
      cta: (form.cta || "").trim() || undefined,
      featured: !!form.featured,
      perks: textToPerks(form.perks),
      sort_order: num(form.sort_order),
      is_trial: !!form.is_trial,
      is_signup_pack: !!form.is_signup_pack,
      is_default_signup_plan: !!form.is_default_signup_plan,
      price_inr: num(form.price_inr),
      price_inr_yearly: num(form.price_inr_yearly),
      // `active` is deliberately NOT sent. The endpoint merges with
      // exclude_unset, so omitting it leaves the stored status exactly as it is
      // — which is what keeps Activate/Deactivate the only thing that writes it.
      // Re-sending the value the form loaded would also silently revert a status
      // change made from another tab while this modal sat open.
      max_runs_per_month: num(form.max_runs_per_month),
      max_projects: num(form.max_projects),
      max_testers: num(form.max_testers),
      trial_days: num(form.trial_days),
      ai_model_tier: form.ai_model_tier,
      reports: form.reports,
      api_keys: num(form.api_keys),
    };
    const payload = {
      [editing]: Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined)),
    };
    setSaveError("");
    try {
      const updated = await updatePlans(payload);
      setData(updated && typeof updated === "object" ? updated : { ...catalog, ...payload });
    } catch (e) {
      // Do NOT fake success or mutate local state — surface the real error and
      // keep the modal open so the admin can retry.
      setBusy(false);
      setSaveError(e.message || "Couldn't save changes. Please try again.");
      return;
    }
    setBusy(false);
    setConfirmOpen(false);
    setEditing(null);
    setSaved(true);
    // Cleared on unmount so a navigation inside the window can't set state on a
    // dead tree.
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  }

  // ---- Add new plan (#81) ---------------------------------------------------
  const setCreate = (k, v) => setCreateForm((f) => ({ ...f, [k]: v }));

  function openCreate() {
    setCreateForm(BLANK_PLAN_FORM);
    setCreateFieldErrors({});
    setCreateSaveError("");
    setCreateOpen(true);
  }

  async function submitCreate() {
    if (createBusy) return;
    const errors = planFormErrors(createForm);
    setCreateFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setCreateBusy(true);
    setCreateSaveError("");
    const payload = {
      name: createForm.name.trim(),
      label: (createForm.label || "").trim() || null,
      blurb: createForm.blurb || null,
      cta: (createForm.cta || "Subscribe").trim(),
      featured: !!createForm.featured,
      perks: textToPerks(createForm.perks),
      sort_order: Number(createForm.sort_order) || 100,
      is_trial: !!createForm.is_trial,
      is_signup_pack: !!createForm.is_signup_pack,
      is_default_signup_plan: !!createForm.is_default_signup_plan,
      price_inr: isBlank(createForm.price_inr) ? null : Number(createForm.price_inr),
      price_inr_yearly: isBlank(createForm.price_inr_yearly) ? null : Number(createForm.price_inr_yearly),
      active: !!createForm.active,
      max_runs_per_month: Number(createForm.max_runs_per_month),
      max_projects: Number(createForm.max_projects),
      max_testers: Number(createForm.max_testers),
      trial_days: Number(createForm.trial_days),
      ai_model_tier: createForm.ai_model_tier,
      reports: createForm.reports,
      api_keys: Number(createForm.api_keys),
    };
    try {
      const updated = await createPlan(payload);
      setData(updated && typeof updated === "object" ? updated : catalog);
    } catch (e) {
      setCreateBusy(false);
      setCreateSaveError(e.message || "Couldn't create the plan. Please try again.");
      return;
    }
    setCreateBusy(false);
    setCreateOpen(false);
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  }

  // ---- Delete plan (#85) ------------------------------------------------------
  // Only a plan an admin created themselves can be removed — the 4 built-ins
  // are wired into signup, billing enforcement, and this page's own styling by
  // id, not just by catalog entry.
  async function confirmDelete() {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const updated = await deletePlan(deleteTarget.id);
      setData(updated && typeof updated === "object" ? updated : catalog);
    } catch (e) {
      setDeleteBusy(false);
      // Surfaces the backend's own reason verbatim — e.g. "N companies are
      // still on this plan" or a 400 refusing a built-in — rather than a
      // generic failure message that hides why it was refused.
      setDeleteError(e.message || "Couldn't delete the plan. Please try again.");
      return;
    }
    setDeleteBusy(false);
    setDeleteTarget(null);
  }

  // ---- Activate / Deactivate --------------------------------------------------
  // Reuses PUT /admin/plans — the ONE mechanism that writes plan status. The
  // endpoint merges with exclude_unset, so sending nothing but `active` is a
  // genuine partial update: no other field of the plan is touched, and it is
  // audited as PLAN.UPDATE exactly like an edit. No second endpoint, and no
  // second copy of the rule.
  async function confirmStatus() {
    if (!statusTarget || statusBusy) return;
    const next = !statusTarget.active;
    setStatusBusy(true);
    setStatusError("");
    try {
      const updated = await updatePlans({ [statusTarget.id]: { active: next } });
      // The server's own catalog is what the cards re-render from, so the badge
      // and the button label can't drift from what was actually stored.
      setData(updated && typeof updated === "object"
        ? updated
        : { ...catalog, [statusTarget.id]: { ...catalog[statusTarget.id], active: next } });
    } catch (e) {
      setStatusBusy(false);
      setStatusError(e.message || "Couldn't change the plan's status. Please try again.");
      return;
    }
    setStatusBusy(false);
    setStatusTarget(null);
    setSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), 2500);
  }

  const editingPlan = editing ? plans.find((p) => p.id === editing) : null;

  // Non-blocking sanity checks. The operator may have a reason for either, so
  // these inform rather than refuse — nothing previously questioned a yearly
  // price that was worse value than paying monthly.
  const priceHint = (() => {
    const m = Number(form.price_inr), y = Number(form.price_inr_yearly);
    if (!Number.isFinite(m) || !Number.isFinite(y) || !m || !y) return "";
    if (y > m * 12) return `Yearly costs more than 12 monthly payments (₹${(m * 12).toLocaleString("en-IN")}). Customers see no saving.`;
    if (y < m * 12 * 0.5) return `That's over 50% off the monthly price. Check the figure.`;
    return "";
  })();

  return (
    <>
      <PageHeader
        /* "Plans" — the name the sidebar, the breadcrumb (matchNavItem reads
           config/nav.js) and this heading all now agree on. It read "Plan
           Catalog" here alone, so the page announced itself as something the
           navigation had no entry for. */
        title="Plans"
        subtitle="Create, price and retire the subscription plans companies are billed against."
        actions={
          <div className="flex items-center gap-3">
            {saved && (
              <motion.span initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 size={16} /> Changes saved
              </motion.span>
            )}
            <Button size="sm" onClick={openCreate}>
              <Plus size={15} /> Add New Plan
            </Button>
          </div>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-brand-200/70 bg-brand-50/60 px-4 py-3 text-sm text-brand-800 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-200">
        <Info size={16} className="mt-0.5 shrink-0" />
        <p>Edits apply <strong>instantly to every company</strong> on the plan. Each company keeps its used count — so raising a limit just adds the difference to what they have left.</p>
      </div>

      <LoadError message={error} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan, i) => {
            const style = PLAN_STYLE[plan.id] || PLAN_STYLE.free_trial;
            const popular = plan.id === "starter";
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.4, ease: "easeOut" }}
                whileHover={{ y: -4 }}
                className={cn(
                  "card group relative flex flex-col overflow-hidden p-0",
                  popular && "ring-2 ring-brand-500"
                )}
              >
                {/* top accent */}
                <div className={cn("h-1.5 w-full bg-gradient-to-r", style.accent)} />

                <div className="flex flex-1 flex-col gap-4 p-5">
                  {/* POPULAR used to sit absolutely in this same top-right
                      corner and unconditionally hide the active/inactive
                      badge to avoid overlapping it — which meant the Starter
                      plan (the one plan actually marked popular) never showed
                      its status at all (#78). Both now sit side by side. */}
                  {/* min-w-0 + truncate: a flex child otherwise won't shrink
                      below its content's own width, so a long plan name (#83)
                      pushed the badges out past the card's edge instead of
                      being clipped. */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-[0.6875rem] font-bold uppercase tracking-widest text-slate-400" title={plan.label || plan.name}>{plan.label || plan.name}</span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {popular && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-2 py-0.5 text-[0.625rem] font-bold text-white shadow-soft">
                          <Sparkles size={11} /> POPULAR
                        </span>
                      )}
                      <span className={cn("rounded-full px-2 py-0.5 text-[0.625rem] font-bold", plan.active ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" : "bg-slate-200/80 text-slate-500 dark:bg-white/10 dark:text-slate-400")}>
                        {plan.active ? "● ACTIVE" : "● INACTIVE"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={cn("grid h-10 w-10 place-items-center rounded-xl transition-transform group-hover:scale-110", style.chip)}>
                      <style.icon size={20} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200" title={plan.name}>{plan.name}</p>
                      <p className="font-display text-2xl font-bold leading-none text-slate-900 dark:text-white">
                        {priceDisplay(plan.price_inr)}
                        {plan.price_inr ? <span className="text-xs font-normal text-slate-400">/mo</span> : null}
                      </p>
                      {plan.price_inr ? (
                        <p className="mt-0.5 text-xs text-slate-400">
                          {priceDisplay(plan.price_inr_yearly)}/yr
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <ul className="flex-1 space-y-2 border-t border-slate-100 pt-4 dark:border-white/5">
                    {cardFeatures(plan).map((f) => (
                      <li key={f.k} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                          <CheckCircle2 size={13} className="shrink-0 text-brand-500" /> {f.k}
                        </span>
                        <span className={cn("font-semibold text-slate-800 dark:text-white", f.cap && "capitalize")}>{f.v}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Status · Edit · Delete. Delete sits last, behind a wider
                      gap, as an icon only — destructive, so it is identifiable
                      (icon + tooltip + its own confirmation) without competing
                      with the two everyday actions beside it. */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm" className="flex-1"
                      onClick={() => { setStatusTarget(plan); setStatusError(""); }}
                      title={plan.active
                        ? "Stop offering this plan to new customers"
                        : "Offer this plan to new customers again"}
                    >
                      {plan.active ? <><PowerOff size={14} /> Deactivate</> : <><Power size={14} /> Activate</>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(plan)} className="flex-1">
                      <Pencil size={14} /> Edit
                    </Button>
                    {/* Always rendered. It used to be omitted entirely for the
                        four built-ins, so on a catalog that has only those —
                        every deployment until someone adds a plan — the page
                        showed no Delete anywhere and read as "the feature is
                        missing" rather than "this particular plan can't go".
                        Disabled + explained is the honest state. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!!plan.is_system}
                      onClick={() => { setDeleteTarget(plan); setDeleteError(""); }}
                      className="ml-1 shrink-0 !px-2 !text-slate-400 hover:!bg-rose-50 hover:!text-rose-600 dark:hover:!bg-rose-500/10 dark:hover:!text-rose-300"
                      title={plan.is_system ? SYSTEM_DELETE_HINT : `Delete ${plan.name}`}
                      aria-label={plan.is_system ? SYSTEM_DELETE_HINT : `Delete ${plan.name}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* edit modal */}
      <Modal
        open={!!editing}
        onClose={() => { setEditing(null); setSaveError(""); setFieldErrors({}); }}
        title={editingPlan ? `Edit — ${editingPlan.name}` : ""}
        size="md"
        footer={
          <div className="flex flex-col gap-2">
            {/* Every invalid field at once, not just the first (#79/#80) — each
                message also highlights its own input below. */}
            {Object.keys(fieldErrors).length > 0 && (
              <ul className="space-y-0.5">
                {Object.values(fieldErrors).map((msg) => (
                  <li key={msg} className="text-xs font-medium text-rose-500">{msg}</li>
                ))}
              </ul>
            )}
            {saveError && (
              <p className="text-xs font-medium text-rose-500">{saveError}</p>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-400">Applies to all companies on this plan</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEditing(null); setSaveError(""); setFieldErrors({}); }}>Cancel</Button>
                <Button size="sm" onClick={requestSave} disabled={busy}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save plan
                </Button>
              </div>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Plan name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} error={fieldErrors.name} maxLength={PLAN_NAME_MAX_LEN} />

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Monthly price (₹) — blank = “Custom”</span>
            <span className="relative block">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
              <input type="number" step="1" min="0" aria-label="Monthly price in rupees" placeholder="0" value={form.price_inr ?? ""} onChange={(e) => set("price_inr", e.target.value)}
                aria-invalid={fieldErrors.price_inr ? "true" : undefined}
                className={cn(
                  "h-11 w-full rounded-xl border bg-white pl-8 pr-4 text-sm text-slate-900 focus:outline-none dark:bg-white/5 dark:text-white",
                  fieldErrors.price_inr ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-brand-500 dark:border-white/10"
                )} />
            </span>
            {fieldErrors.price_inr && <p className="mt-1 text-xs font-medium text-rose-500">{fieldErrors.price_inr}</p>}
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Yearly price (₹) — blank = “Custom” (#89/#90)</span>
            <span className="flex items-center gap-2">
              <span className="relative block flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <input type="number" step="1" min="0" aria-label="Yearly price in rupees" placeholder="0" value={form.price_inr_yearly ?? ""} onChange={(e) => set("price_inr_yearly", e.target.value)}
                  aria-invalid={fieldErrors.price_inr_yearly ? "true" : undefined}
                  className={cn(
                    "h-11 w-full rounded-xl border bg-white pl-8 pr-4 text-sm text-slate-900 focus:outline-none dark:bg-white/5 dark:text-white",
                    fieldErrors.price_inr_yearly ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-brand-500 dark:border-white/10"
                  )} />
              </span>
              <button
                type="button"
                title="Reset to 20% off monthly × 12"
                onClick={() => set("price_inr_yearly", String(yearlyAuto(form.price_inr)))}
                className="h-11 shrink-0 rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-600 hover:border-brand-400 hover:text-brand-600 dark:border-white/10 dark:text-slate-300"
              >
                Auto (-20%)
              </button>
            </span>
            {fieldErrors.price_inr_yearly && <p className="mt-1 text-xs font-medium text-rose-500">{fieldErrors.price_inr_yearly}</p>}
            {/* Informational, not a block — an operator may have a reason for
                either shape. Nothing previously questioned a yearly price that
                was worse value than paying monthly. */}
            {priceHint && (
              <span className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {priceHint}
              </span>
            )}
          </label>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Enforced limits</p>
            <div className="space-y-2">
              <LimitField label="Max runs / mo" value={form.max_runs_per_month} onChange={(v) => set("max_runs_per_month", v)} error={fieldErrors.max_runs_per_month} />
              <LimitField label="Projects" value={form.max_projects} onChange={(v) => set("max_projects", v)} error={fieldErrors.max_projects} />
              <LimitField label="Team seats" value={form.max_testers} onChange={(v) => set("max_testers", v)} error={fieldErrors.max_testers} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Catalog details</p>
            <div className="space-y-2">
              <Field label="Trial days" error={fieldErrors.trial_days}>
                <input type="number" min="0" value={form.trial_days ?? 0} onChange={(e) => set("trial_days", e.target.value)}
                  aria-invalid={fieldErrors.trial_days ? "true" : undefined}
                  className={cn(inputCls, fieldErrors.trial_days && "border-rose-400 focus:border-rose-500")} />
              </Field>
              <Field label="AI model tier">
                <select value={form.ai_model_tier} onChange={(e) => set("ai_model_tier", e.target.value)} className={inputCls}>
                  <option value="basic">basic</option>
                  <option value="advanced">advanced</option>
                </select>
              </Field>
              <Field label="Reports">
                <input value={form.reports || ""} onChange={(e) => set("reports", e.target.value)} className={inputCls} />
              </Field>
              <LimitField label="API keys" value={form.api_keys} onChange={(v) => set("api_keys", v)} error={fieldErrors.api_keys} />
            </div>
          </div>

          {/* Status is READ-ONLY here. It was an editable checkbox, which made
              this form a second way to write the same field the card's
              Activate/Deactivate button owns — two controls for one piece of
              state, one of them buried behind an unrelated form and confirmed
              with a generic "apply changes". Status now has a single path
              (still the same PUT /admin/plans underneath), and this shows the
              current value so the form isn't silent about it. */}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5 dark:border-white/10 dark:bg-white/5">
            <span className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <span className={cn("h-2 w-2 shrink-0 rounded-full", editingPlan?.active ? "bg-emerald-500" : "bg-slate-400")} />
              {editingPlan?.active ? "Active — offered to customers" : "Inactive — not offered to customers"}
            </span>
            <span className="text-xs text-slate-400">
              Change with {editingPlan?.active ? "Deactivate" : "Activate"} on the plan card
            </span>
          </div>

          <CatalogFields values={form} set={set} />
        </div>
      </Modal>

      {/* Confirm before writing. The banner above says these edits "apply
          instantly to every company on the plan" — a single unguarded click was
          not a fair match for that. Shows the exact diff, so an accidental edit
          to a field the operator never meant to touch is visible before it
          reaches the catalog every limit check resolves against. */}
      <Modal
        open={confirmOpen}
        onClose={() => (busy ? null : setConfirmOpen(false))}
        title={`Apply changes to ${editingPlan?.name || editing}?`}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" variant={zeroLimits.length ? "danger" : "primary"} onClick={saveEdit} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {zeroLimits.length ? "Save anyway" : "Save changes"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {zeroLimits.length > 0 && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/70 p-3 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <p>
                <strong>{zeroLimits.join(", ")}</strong> {zeroLimits.length === 1 ? "is" : "are"} set to
                0. Every company on this plan hits that cap immediately and can&apos;t run a single
                test. Leave the field blank instead to keep the current value.
              </p>
            </div>
          )}
          {pendingChanges.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Nothing has changed.</p>
          ) : (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                This takes effect immediately for every company on the plan. Each company keeps its
                used count, so raising a limit just adds the difference to what they have left.
              </p>
              <ul className="space-y-1.5 rounded-xl border border-slate-200 p-3 text-sm dark:border-white/10">
                {pendingChanges.map((c) => (
                  <li key={c.key} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-slate-700 dark:text-slate-200">{c.label}</span>
                    <span className="text-slate-400 line-through">{String(c.from ?? "—")}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-semibold text-slate-900 dark:text-white">{String(c.to)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400">
                Fields left blank in the form are not sent and keep their current value.
              </p>
            </>
          )}
        </div>
      </Modal>

      {/* Add new plan (#81) — a superadmin isn't limited to the 4 built-in
          tiers; this creates a genuinely new plan id in the catalog, which
          then shows up everywhere the catalog is read from (this page,
          Change Plan, signup, the public pricing endpoint). */}
      <Modal
        open={createOpen}
        onClose={() => (createBusy ? null : setCreateOpen(false))}
        title="Add New Plan"
        size="md"
        footer={
          <div className="flex flex-col gap-2">
            {Object.keys(createFieldErrors).length > 0 && (
              <ul className="space-y-0.5">
                {Object.values(createFieldErrors).map((msg) => (
                  <li key={msg} className="text-xs font-medium text-rose-500">{msg}</li>
                ))}
              </ul>
            )}
            {createSaveError && (
              <p className="text-xs font-medium text-rose-500">{createSaveError}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} disabled={createBusy}>Cancel</Button>
              <Button size="sm" onClick={submitCreate} disabled={createBusy}>
                {createBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create plan
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <Input label="Plan name" value={createForm.name || ""} onChange={(e) => setCreate("name", e.target.value)} error={createFieldErrors.name} maxLength={PLAN_NAME_MAX_LEN} />

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Monthly price (₹) — blank = “Custom”</span>
            <span className="relative block">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
              <input type="number" step="1" min="0" aria-label="Monthly price in rupees" placeholder="0" value={createForm.price_inr ?? ""} onChange={(e) => setCreate("price_inr", e.target.value)}
                aria-invalid={createFieldErrors.price_inr ? "true" : undefined}
                className={cn(
                  "h-11 w-full rounded-xl border bg-white pl-8 pr-4 text-sm text-slate-900 focus:outline-none dark:bg-white/5 dark:text-white",
                  createFieldErrors.price_inr ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-brand-500 dark:border-white/10"
                )} />
            </span>
            {createFieldErrors.price_inr && <p className="mt-1 text-xs font-medium text-rose-500">{createFieldErrors.price_inr}</p>}
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Yearly price (₹) — blank = “Custom”</span>
            <span className="flex items-center gap-2">
              <span className="relative block flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                <input type="number" step="1" min="0" aria-label="Yearly price in rupees" placeholder="0" value={createForm.price_inr_yearly ?? ""} onChange={(e) => setCreate("price_inr_yearly", e.target.value)}
                  aria-invalid={createFieldErrors.price_inr_yearly ? "true" : undefined}
                  className={cn(
                    "h-11 w-full rounded-xl border bg-white pl-8 pr-4 text-sm text-slate-900 focus:outline-none dark:bg-white/5 dark:text-white",
                    createFieldErrors.price_inr_yearly ? "border-rose-400 focus:border-rose-500" : "border-slate-300 focus:border-brand-500 dark:border-white/10"
                  )} />
              </span>
              <button
                type="button"
                title="Set to 20% off monthly × 12"
                onClick={() => setCreate("price_inr_yearly", String(yearlyAuto(createForm.price_inr)))}
                className="h-11 shrink-0 rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-600 hover:border-brand-400 hover:text-brand-600 dark:border-white/10 dark:text-slate-300"
              >
                Auto (-20%)
              </button>
            </span>
            {createFieldErrors.price_inr_yearly && <p className="mt-1 text-xs font-medium text-rose-500">{createFieldErrors.price_inr_yearly}</p>}
          </label>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Enforced limits</p>
            <div className="space-y-2">
              <LimitField label="Max runs / mo" value={createForm.max_runs_per_month} onChange={(v) => setCreate("max_runs_per_month", v)} error={createFieldErrors.max_runs_per_month} />
              <LimitField label="Projects" value={createForm.max_projects} onChange={(v) => setCreate("max_projects", v)} error={createFieldErrors.max_projects} />
              <LimitField label="Team seats" value={createForm.max_testers} onChange={(v) => setCreate("max_testers", v)} error={createFieldErrors.max_testers} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Catalog details</p>
            <div className="space-y-2">
              <Field label="Trial days" error={createFieldErrors.trial_days}>
                <input type="number" min="0" value={createForm.trial_days ?? 0} onChange={(e) => setCreate("trial_days", e.target.value)}
                  aria-invalid={createFieldErrors.trial_days ? "true" : undefined}
                  className={cn(inputCls, createFieldErrors.trial_days && "border-rose-400 focus:border-rose-500")} />
              </Field>
              <Field label="AI model tier">
                <select value={createForm.ai_model_tier} onChange={(e) => setCreate("ai_model_tier", e.target.value)} className={inputCls}>
                  <option value="basic">basic</option>
                  <option value="advanced">advanced</option>
                </select>
              </Field>
              <Field label="Reports">
                <input value={createForm.reports || ""} onChange={(e) => setCreate("reports", e.target.value)} className={inputCls} />
              </Field>
              <LimitField label="API keys" value={createForm.api_keys} onChange={(v) => setCreate("api_keys", v)} error={createFieldErrors.api_keys} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={createForm.active ?? true} onChange={(e) => setCreate("active", e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            Plan is active (visible to users)
          </label>

          <CatalogFields values={createForm} set={setCreate} />
        </div>
      </Modal>

      {/* Delete plan (#85) — refused server-side (and reported back verbatim)
          when the plan is one of the 4 built-ins, or when any company is still
          on it (current or scheduled via a pending plan change). */}
      <Modal
        open={!!deleteTarget}
        onClose={() => (deleteBusy ? null : setDeleteTarget(null))}
        title={`Delete ${deleteTarget?.name || "plan"}?`}
        size="sm"
        footer={
          <div className="flex flex-col gap-2">
            {deleteError && <p className="text-xs font-medium text-rose-500">{deleteError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</Button>
              <Button size="sm" variant="danger" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete plan
              </Button>
            </div>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This removes <strong>{deleteTarget?.name}</strong> from the catalog for good — it will no
          longer be offered on signup or in Change Plan. This can&apos;t be undone.
        </p>
      </Modal>

      {/* Activate / Deactivate. Same Modal-confirm shape as the two above, and
          deliberately NOT variant="danger": taking a plan off sale is reversible
          with one click, unlike the delete beside it, and dressing both the same
          way would flatten that difference.

          Everything the body claims is what the code does, not a guess:
            · GET /billing/plans filters on `active`, so an inactive plan leaves
              the pricing surfaces;
            · _purchasable_plan() 400s an order for it ("no longer available"),
              so a stale tab can't check out;
            · nothing in entitlement reads `active` — effective_plan_id() and
              get_plan_limits() resolve by id alone — so companies already on the
              plan keep it, at the same limits, and keep renewing. */}
      <Modal
        open={!!statusTarget}
        onClose={() => (statusBusy ? null : setStatusTarget(null))}
        title={statusTarget?.active
          ? `Deactivate ${statusTarget?.name}?`
          : `Activate ${statusTarget?.name}?`}
        size="sm"
        footer={
          <div className="flex flex-col gap-2">
            {statusError && <p className="text-xs font-medium text-rose-500">{statusError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setStatusTarget(null)} disabled={statusBusy}>
                Cancel
              </Button>
              <Button size="sm" onClick={confirmStatus} disabled={statusBusy}>
                {statusBusy
                  ? <Loader2 size={15} className="animate-spin" />
                  : statusTarget?.active ? <PowerOff size={15} /> : <Power size={15} />}
                {statusTarget?.active ? "Deactivate plan" : "Activate plan"}
              </Button>
            </div>
          </div>
        }
      >
        {statusTarget?.active ? (
          <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <p>
              <strong>{statusTarget?.name}</strong> stops being offered: it disappears from the
              pricing page and Change Plan, and any checkout still holding it is refused.
            </p>
            <p>
              Companies already on it <strong>keep it</strong> — same limits, and it keeps
              renewing. You can activate it again at any time.
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <strong>{statusTarget?.name}</strong> goes back on sale — it reappears on the pricing
            page and in Change Plan, and can be bought again at its current price and limits.
          </p>
        )}
      </Modal>

    </>
  );
}

const inputCls = "h-9 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white";

// Presentation + catalog placement + the signup flags, shared by the Edit and
// Add forms so the two can't offer different sets of fields.
//
// The three checkboxes at the bottom are what replaced hardcoded plan ids in the
// backend: `is_trial` used to be "the id is free_trial", `is_signup_pack` used to
// be PAID_SIGNUP_PLANS = {starter, growth}. The server still has the final say —
// it refuses a default that is inactive or not a trial, and clears the previous
// default in the same transaction — so these are a request, not a guarantee.
function CatalogFields({ values, set }) {
  const cb = "h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500";
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 p-3.5 dark:border-white/10">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Catalog &amp; signup
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Card label">
          <input value={values.label || ""} onChange={(e) => set("label", e.target.value)}
                 placeholder="STARTER" maxLength={100} className={inputCls} />
        </Field>
        <Field label="Sort order">
          {/* Lower sorts first. Ties fall back to name then id, so the order is
              stable even if two plans share a number. */}
          <input type="number" min="0" step="10" value={values.sort_order ?? 100}
                 onChange={(e) => set("sort_order", e.target.value)} className={inputCls} />
        </Field>
      </div>

      <Field label="Pricing-page blurb">
        <input value={values.blurb || ""} onChange={(e) => set("blurb", e.target.value)}
               maxLength={500} placeholder="One line shown under the plan name"
               className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Button text">
          <input value={values.cta || ""} onChange={(e) => set("cta", e.target.value)}
                 maxLength={50} placeholder="Subscribe" className={inputCls} />
        </Field>
        <Field label="Perks (one per line)">
          <textarea rows={3} value={values.perks || ""} onChange={(e) => set("perks", e.target.value)}
                    placeholder={"Priority support\nAdvanced analytics"}
                    className={inputCls + " h-auto py-2 leading-snug"} />
        </Field>
      </div>

      <div className="space-y-2 border-t border-slate-100 pt-3 dark:border-white/5">
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" className={cb + " mt-0.5"} checked={!!values.featured}
                 onChange={(e) => set("featured", e.target.checked)} />
          <span>Highlight on the pricing page
            <span className="block text-xs text-slate-400">Renders as the featured card.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" className={cb + " mt-0.5"} checked={!!values.is_trial}
                 onChange={(e) => set("is_trial", e.target.checked)} />
          <span>Trial plan
            <span className="block text-xs text-slate-400">
              You can have as many trial plans as you like.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" className={cb + " mt-0.5"} checked={!!values.is_signup_pack}
                 onChange={(e) => set("is_signup_pack", e.target.checked)} />
          <span>Offer on the signup screen
            <span className="block text-xs text-slate-400">
              Sold as a pack when someone registers.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" className={cb + " mt-0.5"} checked={!!values.is_default_signup_plan}
                 onChange={(e) => set("is_default_signup_plan", e.target.checked)} />
          <span>Default plan for new companies
            <span className="block text-xs text-slate-400">
              Only one plan can hold this, and it has to be an active trial plan.
              Choosing it here moves it off whichever plan holds it now.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

function Field({ label, children, error }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="w-24 shrink-0 text-xs text-slate-500 dark:text-slate-400 sm:w-32">{label}</span>
        {children}
      </div>
      {error && <p className="ml-[6.5rem] mt-1 text-xs font-medium text-rose-500 sm:ml-[8.5rem]">{error}</p>}
    </div>
  );
}

function LimitField({ label, value, onChange, error }) {
  const unlimited = Number(value) >= UNLIMITED;
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="w-24 shrink-0 text-xs text-slate-500 dark:text-slate-400 sm:w-32">{label}</span>
        <input
          type="number"
          min="0"
          value={unlimited ? "" : value}
          placeholder={unlimited ? "Unlimited" : "0"}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? "true" : undefined}
          className={cn(inputCls, error && "border-rose-400 focus:border-rose-500")}
        />
        <button
          type="button"
          onClick={() => onChange(unlimited ? "1" : String(UNLIMITED))}
          title="Toggle unlimited"
          className={cn(
            "grid h-9 w-9 shrink-0 place-items-center rounded-lg border text-xs font-bold transition-colors",
            unlimited ? "border-brand-500 bg-brand-600 text-white" : "border-slate-300 text-slate-500 hover:border-brand-400 hover:text-brand-600 dark:border-white/10 dark:text-slate-300"
          )}
        >
          <InfinityIcon size={15} />
        </button>
      </div>
      {error && <p className="ml-[6.5rem] mt-1 text-xs font-medium text-rose-500 sm:ml-[8.5rem]">{error}</p>}
    </div>
  );
}
